import { api } from './api';
import type {
  AdminUserResponse,
  AdminUserDetailResponse,
  BlockWarningResponse,
  AdminActionResponse,
  CursorPageResponse,
  GarageSummary,
  CustomerTermsAcceptance,
} from '../types';

interface ListUsersParams {
  search?: string;
  role?: string;
  status?: string;
  cursor?: string;
  size?: number;
}

export interface ListAdminActionsParams {
  /** Catalogue codes, comma separated (the API also accepts the parameter repeated). */
  actionType?: string;
  entityType?: string;
  q?: string;
  /** `YYYY-MM-DD`, Asia/Dubai, inclusive (Spec 069). */
  dateFrom?: string;
  dateTo?: string;
  cursor?: string;
  size?: number;
}

interface ListOrdersParams {
  customerId?: string;
  search?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  cursor?: string;
  size?: number;
}

function buildQuery(params: Record<string, unknown>): string {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) {
      query.set(key, String(value));
    }
  });
  const str = query.toString();
  return str ? `?${str}` : '';
}

export const adminUsersApi = {
  listUsers: (params: ListUsersParams, token: string) => {
    const query = buildQuery({ ...params });
    return api.get<CursorPageResponse<AdminUserResponse>>(`/api/admin/users${query}`, token);
  },

  getUserDetail: (userId: string, token: string) => {
    return api.get<AdminUserDetailResponse>(`/api/admin/users/${userId}`, token);
  },

  getUserDetailBySubject: (subjectId: string, token: string) => {
    return api.get<AdminUserDetailResponse>(`/api/admin/users/by-subject/${encodeURIComponent(subjectId)}`, token);
  },

  resetPassword: (subjectId: string, token: string) => {
    return api.post<void>(`/api/admin/auth/users/${subjectId}/reset-password`, {}, token);
  },

  blockUser: (userId: string, reason: string, token: string) => {
    return api.post<AdminUserDetailResponse>(`/api/admin/users/${userId}/block`, { reason }, token);
  },

  unblockUser: (userId: string, token: string) => {
    return api.post<AdminUserDetailResponse>(`/api/admin/users/${userId}/unblock`, {}, token);
  },

  getBlockWarning: (userId: string, token: string) => {
    return api.get<BlockWarningResponse>(`/api/admin/users/${userId}/block-warning`, token);
  },

  getUserGarages: (ownerId: string, token: string) => {
    return api.get<{ garages: GarageSummary[] }>(`/api/garages?ownerId=${encodeURIComponent(ownerId)}`, token)
      .then(res => res.garages || []);
  },

  getUserOrders: (params: ListOrdersParams, token: string) => {
    const query = buildQuery({ ...params });
    return api.get<CursorPageResponse<AdminOrderSummary>>(`/api/admin/orders${query}`, token);
  },

  getAdminActions: (userId: string, params: ListAdminActionsParams, token: string) => {
    const query = buildQuery({ ...params });
    return api.get<CursorPageResponse<AdminActionResponse>>(`/api/admin/users/${userId}/admin-actions${query}`, token);
  },

  /** #1165 — terms the customer accepted, newest first; [] when none, 404 for an unknown subject. */
  getCustomerTermsAcceptances: (subjectId: string, token: string) => {
    return api.get<CustomerTermsAcceptance[]>(
      `/api/admin/terms-documents/customers/${encodeURIComponent(subjectId)}/acceptances`, token);
  },
};

export interface AdminOrderSummary {
  orderId: string;
  status: string;
  totalAmount: number;
  currency: string;
  createdAt: string;
  completedAt?: string;
}
